import { useCallback, useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { Download, Search, Ban, Trash2, UserPlus, Sparkles } from "lucide-react";
import { Link } from "react-router-dom";
import { api, errMsg, eur, fmtDate, fmtDateTime, KIND_LABELS, REFUND_MSG, ROLE_LABELS, STATUS_LABELS, TIME_BANDS } from "@/lib/api";
import { Brand, UserMenu } from "@/components/Shell";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { StatusBadge } from "@/components/StatusBadge";
import { AuthzUsers, Verifications } from "@/components/admin/AuthzPanel";
import { SuperAdmins } from "@/components/admin/SuperAdmins";
import { OperatorsApproval } from "@/components/admin/OperatorsApproval";
import { CafeModeration } from "@/components/admin/CafeModeration";
import { ServicesAdmin } from "@/components/admin/ServicePricing";
import { CommandHeader, useHealth } from "@/components/command/CommandHeader";
import { KpiGrid } from "@/components/command/KpiGrid";
import { Charts } from "@/components/command/Charts";
import { AdminChat } from "@/components/command/AdminChat";
import { AutomationControl } from "@/components/command/AutomationControl";
import { ActivityTimeline } from "@/components/command/ActivityTimeline";
import { JobSearchPanel } from "@/components/command/JobSearchPanel";
import { WalletAdmin } from "@/components/wallet/WalletAdmin";

const run = async (fn, ok, reload) => {
  try {
    await fn();
    toast.success(ok);
    reload?.();
  } catch (e) {
    toast.error(errMsg(e));
  }
};

async function downloadCsv(name) {
  const res = await api.get(`/superadmin/export/${name}.csv`, { responseType: "blob" });
  const a = document.createElement("a");
  a.href = URL.createObjectURL(res.data);
  a.download = `${name}.csv`;
  a.click();
}

function Overview() {
  const [o, setO] = useState(null);
  useEffect(() => {
    api.get("/superadmin/overview").then(({ data }) => setO(data));
  }, []);
  if (!o) return null;
  return (
    <div className="space-y-2" data-testid="sa-overview">
      <p className="text-xs font-bold uppercase tracking-[0.18em] text-slate-500">Prenotazioni per stato · rimborsi da gestire: {o.rimborsi_da_gestire} · utenti sospesi: {o.utenti_sospesi}</p>
      <div className="flex flex-wrap gap-2">{Object.entries(o.prenotazioni_per_stato).map(([k, v]) => <span key={k} className="rounded-full border border-slate-200 bg-white px-3 py-1 text-xs">{STATUS_LABELS[k] || k}: <b>{v}</b></span>)}</div>
    </div>
  );
}

function BookingRow({ a, reload }) {
  const [reason, setReason] = useState("");
  const [pct, setPct] = useState(100);
  const [open, setOpen] = useState(false);
  const active = !["annullato", "completato"].includes(a.status);
  return (
    <div className="rounded-xl border border-slate-200 bg-white p-3" data-testid={`sa-booking-${a.id}`}>
      <div className="flex flex-wrap items-center justify-between gap-2 text-xs">
        <span><b>{a.patient_name}</b> · {KIND_LABELS[a.kind]} · {a.service_name} · {a.professional_name} · {fmtDate(a.date)} {a.time || TIME_BANDS[a.time_band]} · {eur(a.amount)}</span>
        <span className="flex items-center gap-2"><StatusBadge status={a.status} /><StatusBadge status={a.payment_status} />
          {active && <Button size="sm" variant="outline" data-testid={`sa-booking-cancel-${a.id}`} onClick={() => setOpen(!open)}><Ban className="mr-1 h-3.5 w-3.5" />Annulla</Button>}
        </span>
      </div>
      {open && (
        <div className="mt-2 flex flex-wrap items-end gap-2">
          <Input className="h-8 max-w-xs text-xs" data-testid={`sa-cancel-reason-${a.id}`} placeholder="Motivo (comunicato al paziente)" value={reason} onChange={(e) => setReason(e.target.value)} />
          {["pagato", "rimborsato_parziale"].includes(a.payment_status) && <Input className="h-8 w-28 text-xs" type="number" min={0} max={100} data-testid={`sa-cancel-pct-${a.id}`} value={pct} onChange={(e) => setPct(Number(e.target.value))} title="% rimborso" />}
          <Button size="sm" data-testid={`sa-cancel-confirm-${a.id}`} onClick={() => run(() => api.post(`/superadmin/bookings/${a.id}/cancel`, { reason, refund_pct: pct }), "Prenotazione annullata", reload)} className="bg-rose-600 hover:bg-rose-700">Conferma annullamento{a.payment_status === "pagato" ? ` e rimborso ${pct}%` : ""}</Button>
        </div>
      )}
    </div>
  );
}

function Bookings() {
  const [f, setF] = useState({ status: "", kind: "", q: "" });
  const [items, setItems] = useState([]);
  const fRef = useRef(f);
  fRef.current = f;
  const load = useCallback(() => api.get("/superadmin/bookings", { params: fRef.current }).then(({ data }) => setItems(data)), []);
  useEffect(() => {
    load();
  }, [f.status, f.kind, load]);
  return (
    <div className="space-y-3" data-testid="sa-bookings">
      <div className="flex flex-wrap gap-2">
        <div className="relative"><Search className="absolute left-2.5 top-2.5 h-4 w-4 text-slate-400" /><Input data-testid="sa-bookings-search" className="w-60 pl-8" placeholder="Paziente, professionista, servizio" value={f.q} onChange={(e) => setF({ ...f, q: e.target.value })} onKeyDown={(e) => e.key === "Enter" && load()} /></div>
        <Select value={f.status || "all"} onValueChange={(v) => setF({ ...f, status: v === "all" ? "" : v })}>
          <SelectTrigger className="w-52" data-testid="sa-bookings-status"><SelectValue /></SelectTrigger>
          <SelectContent><SelectItem value="all">Tutti gli stati</SelectItem>{["richiesta_inviata", "in_attesa_pagamento", "confermato", "completato", "annullato", "rifiutato"].map((s) => <SelectItem key={s} value={s}>{STATUS_LABELS[s]}</SelectItem>)}</SelectContent>
        </Select>
        <Select value={f.kind || "all"} onValueChange={(v) => setF({ ...f, kind: v === "all" ? "" : v })}>
          <SelectTrigger className="w-48" data-testid="sa-bookings-kind"><SelectValue /></SelectTrigger>
          <SelectContent><SelectItem value="all">Tutte le tipologie</SelectItem>{Object.entries(KIND_LABELS).map(([k, l]) => <SelectItem key={k} value={k}>{l}</SelectItem>)}</SelectContent>
        </Select>
        <Button variant="outline" data-testid="sa-export-bookings" onClick={() => downloadCsv("prenotazioni")}><Download className="mr-1 h-4 w-4" />CSV</Button>
      </div>
      {!items.length && <p className="text-sm text-slate-500">Nessuna prenotazione.</p>}
      {items.map((a) => <BookingRow key={a.id} a={a} reload={load} />)}
    </div>
  );
}

function Cancellations() {
  const [items, setItems] = useState([]);
  const load = () => api.get("/superadmin/cancellations").then(({ data }) => setItems(data));
  useEffect(() => {
    load();
  }, []);
  return (
    <div className="space-y-2" data-testid="sa-cancellations">
      {!items.length && <p className="text-sm text-slate-500">Nessuna cancellazione.</p>}
      {items.map((a) => (
        <div key={a.id} className="flex flex-wrap items-center justify-between gap-2 rounded-xl border border-slate-200 bg-white p-3 text-xs" data-testid={`sa-cancellation-${a.id}`}>
          <span><b>{a.patient_name}</b> · {a.service_name} · {fmtDate(a.date)} · annullato da {a.cancelled_by || "-"} il {fmtDateTime(a.cancelled_at)} {a.cancel_reason ? `· ${a.cancel_reason}` : ""}</span>
          <span className="flex items-center gap-2"><StatusBadge status={a.payment_status} />
            {a.payment_status === "rimborso_richiesto" && <Button size="sm" data-testid={`sa-approve-refund-${a.id}`} onClick={() => run(() => api.post(`/superadmin/cancellations/${a.id}/approve-refund`), "Rimborso gestito", load)} className="bg-sky-600 hover:bg-sky-700">Approva rimborso {eur(a.refund_amount)}</Button>}
          </span>
        </div>
      ))}
    </div>
  );
}

function Payments() {
  const [items, setItems] = useState([]);
  const [amt, setAmt] = useState({});
  const load = () => api.get("/payments/admin/all").then(({ data }) => setItems(data));
  useEffect(() => {
    load();
  }, []);
  return (
    <div className="space-y-2" data-testid="sa-payments">
      <div className="flex items-center justify-between"><p className="text-xs font-semibold text-amber-700">Provider pagamenti: Stripe (modalità di prova) – nessun movimento di denaro reale.</p><Button variant="outline" data-testid="sa-export-payments" onClick={() => downloadCsv("pagamenti")}><Download className="mr-1 h-4 w-4" />CSV</Button></div>
      {items.map((p) => (
        <div key={p.id} className="flex flex-wrap items-center justify-between gap-2 rounded-xl border border-slate-200 bg-white p-3 text-xs" data-testid={`sa-payment-${p.id}`}>
          <span>{p.receipt_number || p.id} · {p.description} · {eur(p.amount)} {p.refunded_amount ? `· rimborsato ${eur(p.refunded_amount)}` : ""} · {fmtDateTime(p.created_at)}</span>
          <span className="flex items-center gap-2"><StatusBadge status={p.status} />
            {["pagato", "rimborsato_parziale"].includes(p.status) && <>
              <Input className="h-8 w-24 text-xs" type="number" step="0.01" placeholder="€" data-testid={`sa-refund-amount-${p.id}`} value={amt[p.id] || ""} onChange={(e) => setAmt({ ...amt, [p.id]: e.target.value })} />
              <Button size="sm" variant="outline" data-testid={`sa-refund-${p.id}`} onClick={() => api.post(`/superadmin/payments/${p.id}/refund`, { amount: Number(amt[p.id]) }).then(({ data }) => { toast.success(REFUND_MSG[data.refund_mode] || "Rimborso registrato"); load(); }).catch((e) => toast.error(errMsg(e)))}>Rimborsa</Button>
            </>}
          </span>
        </div>
      ))}
    </div>
  );
}

const ASSIGNABLE_ROLES = Object.entries(ROLE_LABELS).filter(([k]) => k !== "super_admin");

function Users() {
  const [f, setF] = useState({ email: "", name: "", role: "segreteria", password: "" });
  const [key, setKey] = useState(0);
  const [eraseId, setEraseId] = useState("");
  return (
    <div className="space-y-5">
      <div className="glass flex flex-wrap items-end gap-2 rounded-2xl p-4" data-testid="sa-create-user">
        <div className="space-y-1"><Label>Nome</Label><Input data-testid="sa-user-name" value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} /></div>
        <div className="space-y-1"><Label>Email</Label><Input data-testid="sa-user-email" value={f.email} onChange={(e) => setF({ ...f, email: e.target.value })} /></div>
        <div className="space-y-1"><Label>Password iniziale</Label><Input data-testid="sa-user-password" type="password" value={f.password} onChange={(e) => setF({ ...f, password: e.target.value })} /></div>
        <Select value={f.role} onValueChange={(r) => setF({ ...f, role: r })}><SelectTrigger className="w-48" data-testid="sa-user-role"><SelectValue /></SelectTrigger><SelectContent>{ASSIGNABLE_ROLES.map(([k, l]) => <SelectItem key={k} value={k}>{l}</SelectItem>)}</SelectContent></Select>
        <Button data-testid="sa-user-create-btn" onClick={() => run(() => api.post("/superadmin/users", f), "Utente creato", () => { setKey(key + 1); setF({ email: "", name: "", role: "segreteria", password: "" }); })} className="bg-sky-600 hover:bg-sky-700"><UserPlus className="mr-1 h-4 w-4" />Crea utente</Button>
      </div>
      <div className="flex flex-wrap items-end gap-2 rounded-2xl border border-rose-200 bg-rose-50 p-4" data-testid="sa-erase-user">
        <div className="space-y-1"><Label>Anonimizza account (conserva prenotazioni senza nome) – User ID</Label><Input data-testid="sa-erase-id" className="w-64" value={eraseId} onChange={(e) => setEraseId(e.target.value)} placeholder="user_…" /></div>
        <Button variant="outline" data-testid="sa-erase-btn" onClick={() => window.confirm("Anonimizzare definitivamente l'account?") && run(() => api.delete(`/superadmin/users/${eraseId}`), "Account anonimizzato", () => setKey(key + 1))} className="text-rose-700"><Trash2 className="mr-1 h-4 w-4" />Anonimizza</Button>
      </div>
      <AuthzUsers key={key} superMode />
    </div>
  );
}

function Rules() {
  const [p, setP] = useState(null);
  useEffect(() => {
    api.get("/superadmin/policy").then(({ data }) => setP(data));
  }, []);
  if (!p) return null;
  const num = (k) => (e) => setP({ ...p, [k]: Number(e.target.value) });
  return (
    <div className="glass max-w-xl space-y-3 rounded-3xl p-5" data-testid="sa-rules">
      <Label>Cancellazione gratuita fino a (ore prima)</Label><Input type="number" data-testid="sa-free-hours" value={p.free_cancel_hours} onChange={num("free_cancel_hours")} />
      <Label>Rimborso per cancellazione tardiva (%)</Label><Input type="number" data-testid="sa-late-pct" value={p.late_cancel_refund_pct} onChange={num("late_cancel_refund_pct")} />
      <Label>Rimborso in caso di mancata presentazione (%)</Label><Input type="number" value={p.no_show_refund_pct} onChange={num("no_show_refund_pct")} />
      <Label>Avviso di intermediazione</Label><Textarea value={p.intermediation_notice} onChange={(e) => setP({ ...p, intermediation_notice: e.target.value })} />
      <Button data-testid="sa-rules-save" onClick={() => run(() => api.put("/superadmin/policy", p), "Regole salvate")} className="rounded-full bg-sky-600 hover:bg-sky-700">Salva regole</Button>
    </div>
  );
}

function CommandOverview() {
  return (
    <div className="space-y-5">
      <KpiGrid />
      <Charts />
      <div className="rounded-2xl bg-white p-4 text-slate-900"><Overview /></div>
    </div>
  );
}

const TABS = [["panoramica", "Command Center", <CommandOverview />, true], ["ai", "Automation Control", <AutomationControl />, true], ["attivita", "Centro attività AI", <ActivityTimeline />, true], ["jobs", "Job Search AI", <JobSearchPanel />, true],
  ["prenotazioni", "Prenotazioni", <Bookings />], ["cancellazioni", "Cancellazioni e rimborsi", <Cancellations />],
  ["cafe", "Café e bacheca", <CafeModeration />], ["pagamenti", "Pagamenti", <Payments />], ["utenti", "Utenti", <Users />], ["servizi", "Servizi e tariffe", <ServicesAdmin />], ["superadmin", "Super Admin", <SuperAdmins />], ["operatori", "Nuovi operatori", <OperatorsApproval />], ["verifiche", "Verifica professionisti", <Verifications superMode />], ["carte", "Carte e Selvix Wallet", <WalletAdmin />, true], ["regole", "Regole di prenotazione", <Rules />]];

export default function SuperAdminPage() {
  const [chat, setChat] = useState(false);
  const [tab, setTab] = useState("panoramica");
  const health = useHealth();
  return (
    <div className="min-h-screen bg-[#0B0F19] text-slate-100 selection:bg-[#00F0FF]/30" data-testid="superadmin-page" style={{ backgroundImage: "radial-gradient(circle at 15% 0%, rgba(0,240,255,.08), transparent 40%), radial-gradient(circle at 90% 30%, rgba(59,130,246,.06), transparent 40%)" }}>
      <div className="sticky top-0 z-40 border-b border-white/5 bg-[#0B0F19]/80 backdrop-blur-xl">
        <div className="mx-auto flex max-w-[1500px] items-center justify-between px-4 py-2.5 sm:px-6"><Link to="/" className="rounded-xl bg-white/95 px-2.5 py-1.5"><Brand /></Link><UserMenu compact /></div>
      </div>
      <main className="mx-auto max-w-[1500px] space-y-5 px-4 py-6 sm:px-6">
        <CommandHeader health={health} onTalk={() => setChat(true)} />
        <Tabs value={tab} onValueChange={setTab}>
          <TabsList className="mb-5 flex h-auto flex-wrap justify-start gap-1 rounded-xl border border-slate-800/80 bg-slate-900/90 p-1.5 backdrop-blur-lg">
            {TABS.map(([k, l]) => <TabsTrigger key={k} value={k} data-testid={`sa-tab-${k}`} className="rounded-lg text-slate-400 data-[state=active]:border data-[state=active]:border-[#00F0FF]/40 data-[state=active]:bg-[#00F0FF]/15 data-[state=active]:text-[#00F0FF] data-[state=active]:shadow-[0_0_12px_rgba(0,240,255,0.25)]">{l}</TabsTrigger>)}
          </TabsList>
          {TABS.map(([k, , el, dark]) => <TabsContent key={k} value={k}>{dark ? el : <div className="rounded-2xl bg-white p-4 text-slate-900 sm:p-6">{el}</div>}</TabsContent>)}
        </Tabs>
      </main>
      {!chat && <button data-testid="sa-chat-fab" onClick={() => setChat(true)} aria-label="Parla con Doctor Selvix AI" className="fixed bottom-5 right-5 z-40 grid h-14 w-14 place-items-center rounded-full bg-gradient-to-br from-[#00F0FF] to-[#00A3FF] text-slate-950 shadow-[0_0_25px_rgba(0,240,255,0.55)] transition-transform hover:scale-105"><Sparkles className="h-6 w-6" /></button>}
      <AdminChat open={chat} onClose={() => setChat(false)} />
    </div>
  );
}
